/**
 * Interaction-latency and long-task observer for the telemetry stream.
 *
 * While started, a PerformanceObserver collects `event` entries (one interaction
 * = the longest entry sharing an `interactionId`) and `longtask` entries, and once
 * a minute records a `perf.summary` event with the window's p75 / max interaction
 * duration and long-task totals. Started and stopped by services/telemetry.js with
 * the stream, so a tab with telemetry off runs no observer at all.
 */

export const PERF_WINDOW_MS = 60_000;
/** Ignore event entries shorter than this (the spec's minimum is 16 ms). */
const EVENT_DURATION_THRESHOLD_MS = 16;

/** p-th percentile (0..1) of `values` by nearest rank; null for an empty list. */
export function percentile(values, p) {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const rank = Math.ceil(p * sorted.length) - 1;
  return sorted[Math.min(sorted.length - 1, Math.max(0, rank))];
}

/** Summarise one window's interaction durations (ms) and long-task durations (ms). */
export function summarizePerfWindow(interactions, longtasks, windowMs = PERF_WINDOW_MS) {
  const round = (v) => (v == null ? null : Math.round(v));
  return {
    event: "perf.summary",
    window_s: Math.round(windowMs / 1000),
    interactions: interactions.length,
    inp_p75_ms: round(percentile(interactions, 0.75)),
    inp_max_ms: round(interactions.length ? Math.max(...interactions) : null),
    longtasks: longtasks.length,
    longtask_total_ms: round(longtasks.reduce((a, b) => a + b, 0)),
    longtask_max_ms: round(longtasks.length ? Math.max(...longtasks) : null),
  };
}

/**
 * Create a perf monitor that hands each window's summary to `record`. Windows
 * with no interactions and no long tasks record nothing.
 */
export function createPerfMonitor({
  record,
  Observer = typeof PerformanceObserver !== "undefined" ? PerformanceObserver : null,
  setIntervalImpl = (fn, ms) => setInterval(fn, ms),
  clearIntervalImpl = (id) => clearInterval(id),
  windowMs = PERF_WINDOW_MS,
} = {}) {
  let observers = [];
  let timer = null;
  let byInteraction = new Map();
  let longtasks = [];

  function observe(type, onEntry, extra = {}) {
    try {
      const supported = Observer.supportedEntryTypes;
      if (Array.isArray(supported) && !supported.includes(type)) return;
      const obs = new Observer((list) => {
        for (const entry of list.getEntries()) onEntry(entry);
      });
      obs.observe({ type, buffered: false, ...extra });
      observers.push(obs);
    } catch {
      /* entry type unsupported in this browser */
    }
  }

  function emit() {
    const interactions = [...byInteraction.values()];
    const tasks = longtasks;
    byInteraction = new Map();
    longtasks = [];
    if (interactions.length === 0 && tasks.length === 0) return;
    try {
      record(summarizePerfWindow(interactions, tasks, windowMs));
    } catch {
      /* never surface */
    }
  }

  return {
    start() {
      if (timer !== null || !Observer) return;
      observe(
        "event",
        (e) => {
          if (!e.interactionId) return;
          const prev = byInteraction.get(e.interactionId) || 0;
          byInteraction.set(e.interactionId, Math.max(prev, e.duration));
        },
        { durationThreshold: EVENT_DURATION_THRESHOLD_MS },
      );
      observe("longtask", (e) => longtasks.push(e.duration));
      timer = setIntervalImpl(emit, windowMs);
    },
    stop() {
      for (const obs of observers) {
        try {
          obs.disconnect();
        } catch {
          /* best effort */
        }
      }
      observers = [];
      if (timer !== null) clearIntervalImpl(timer);
      timer = null;
      byInteraction = new Map();
      longtasks = [];
    },
  };
}
